import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { FiShuffle, FiArrowRight } from "react-icons/fi";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { BLOB_URL } from "../../common/constants";

const BGIMAGE =
  `${BLOB_URL}/HLineWeb/Projects/assets/projects/windows/bg_Image.jpg`;


const FINISHES = [
  { name: "Black", color: "#111111" },
  { name: "Gray", color: "#BEBEBE" },
  { name: "Gold", color: "#c5a059" },
  { name: "Royal", color: "#8e44ad" },
];

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const prefersReducedMotion = useReducedMotion();
  const settings = useSiteSettings();

  const PORTAL_URL = settings?.portalUrl || process.env.REACT_APP_PORTAL_URL;

  const [activeFinish, setActiveFinish] = useState(FINISHES[1]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleRandomColor = () => {
    const randomHex = `#${Math.floor(Math.random() * 16777215)
      .toString(16)
      .padStart(6, "0")}`;
    setActiveFinish({ name: "Random", color: randomHex });
  };

  const paneW =
    window.innerWidth < 640 ? 180 : window.innerWidth < 1024 ? 300 : 530;

  const move = prefersReducedMotion ? 0 : paneW;
  const leftX = useTransform(scrollYProgress, [0, 0.28, 0.45], [0, -move, -move]);
  const rightX = useTransform(scrollYProgress, [0, 0.28, 0.45], [0, move, move]);
  const textY = useTransform(scrollYProgress, [0.05, 0.25], [-80, 0]);
  const textOpacity = useTransform(scrollYProgress, [0.05, 0.25], [0, 1]);
  const textScale = useTransform(scrollYProgress, [0.05, 0.25], [0.98, 1]);

  return (
    <section
      ref={sectionRef}
      className={`relative ${isMobile ? "h-[74.7vh]" : "h-[150vh]"
        }`}
    >
      <div
        className={`${isMobile
          ? "relative h-[70vh]"
          : "sticky top-0 h-[65vh] sm:h-screen overflow-hidden"
          }`}
      >
        <div className="absolute inset-0 -z-10">
          <img
            src={BGIMAGE}
            alt="background"
            className={`w-full h-full object-cover ${isMobile ? "object-[center_top]" : ""
              } opacity-70`}
          />
          <div className="absolute inset-0 bg-black/25" />
        </div>

        {!isMobile && (
          <motion.div
            initial={{ opacity: 0, scale: 0.995 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="absolute inset-0 flex items-center justify-center z-0"
          >
            <svg
              viewBox="0 0 1200 600"
              className="w-full h-full max-w-none"
              preserveAspectRatio="xMidYMid meet"
            >
              <motion.g style={{ x: leftX }}>
                <GlassPane
                  x={48}
                  y={48}
                  w={paneW}
                  h={window.innerWidth < 640 ? 280 : 504}
                  side="left"
                  frameColor={activeFinish.color}
                />
              </motion.g>
              <motion.g style={{ x: rightX }}>
                <GlassPane
                  x={622}
                  y={48}
                  w={paneW}
                  h={window.innerWidth < 640 ? 280 : 504}
                  side="right"
                  frameColor={activeFinish.color}
                />
              </motion.g>
            </svg>
          </motion.div>
        )}

        <motion.div
          style={{ y: textY, opacity: textOpacity, scale: textScale }}
          className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"
        >
          <div
            className={`container px-4 text-center pointer-events-auto ${isMobile ? "pt-32" : ""}`}
          >
            <p className="text-[10px] sm:text-xs tracking-[0.25em] uppercase text-white/90">
              Aluminium Windows • Doors • Facades
            </p>
            <h1 className="mt-2 text-xl sm:text-4xl md:text-6xl font-extrabold leading-tight text-white uppercase tracking-tighter">
              Light. Quiet. Built to Last.
            </h1>
            <p className="mt-2 sm:mt-4 text-sm sm:text-base md:text-lg text-white/90 max-w-2xl mx-auto">
              Slim profiles, high-performance glazing and custom engineering for
              modern architecture.
            </p>

            {/* CTA Button */}
            {settings?.showExploreMaterial && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={!isMobile ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="mt-8"
              >
                <a
                  href={PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-white text-black font-bold rounded-full uppercase tracking-widest text-xs hover:bg-white/90 transition shadow-xl group"
                >
                  Explore Our Material
                  <FiArrowRight className="transition-transform group-hover:translate-x-1" />
                </a>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Color Switcher UI */}
        {!isMobile && (
          <div className="absolute bottom-20 right-10 z-20 flex items-center gap-4 bg-black/40 backdrop-blur-md border border-white/10 p-3 rounded-full overflow-hidden shadow-2xl">
            <span className="text-[10px] font-bold uppercase tracking-widest text-white/60 ml-2">Frame Finish</span>
            <div className="flex gap-2 items-center mr-2">
              {FINISHES.map((f) => (
                <button
                  key={f.name}
                  onClick={() => setActiveFinish(f)}
                  title={f.name}
                  className={`w-6 h-6 rounded-full border-2 transition-all duration-300 ${activeFinish.name === f.name ? "border-white scale-125 shadow-lg" : "border-transparent hover:scale-110"
                    }`}
                  style={{ backgroundColor: f.color }}
                />
              ))}

              <button
                onClick={handleRandomColor}
                title="Shuffle Color"
                className={`ml-1 w-7 h-7 rounded-full bg-gradient-to-tr from-purple-600 to-pink-500 border-2 flex items-center justify-center text-white shadow-lg hover:scale-110 active:scale-95 transition-all duration-300 ${activeFinish.name === "Random" ? "border-white scale-110 ring-4 ring-white/20" : "border-transparent"
                  }`}
              >
                <FiShuffle size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function GlassPane({ x, y, w, h, side, frameColor }) {
  const sash = 12,
    frame = 8,
    gridStroke = 1.6;
  const innerX = x + sash,
    innerY = y + sash,
    innerW = w - sash * 2,
    innerH = h - sash * 2;

  const handleScale = w < 250 ? 0.38 : 0.52;
  const hx = side === "left" ? x + w - (sash + 14) : x + (sash + 14);
  const hy = y + h / 2;
  const gradientId = `glass-${x}-${side}`;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill="rgba(255,255,255,0.06)"
        stroke={frameColor}
        strokeWidth={frame}
        className="transition-all duration-500"
      />

      <defs>
        <linearGradient
          id={gradientId}
          x1={innerX}
          y1={innerY}
          x2={innerX + innerW}
          y2={innerY}
        >
          {side === "left" ? (
            <>
              <stop offset="0%" stopColor="rgba(255,255,255,0.18)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
            </>
          ) : (
            <>
              <stop offset="0%" stopColor="rgba(255,255,255,0.05)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.18)" />
            </>
          )}
        </linearGradient>
      </defs>

      <rect
        x={innerX}
        y={innerY}
        width={innerW}
        height={innerH}
        fill={`url(#${gradientId})`}
        stroke="rgba(255,255,255,0.14)"
        strokeWidth="1.5"
      />

      {[1, 2].map((i) => (
        <rect
          key={`v${i}`}
          x={innerX + (innerW * i) / 3 - gridStroke / 2}
          y={innerY + 1}
          width={gridStroke}
          height={innerH - 2}
          fill={frameColor}
          opacity="0.25"
          className="transition-all duration-500"
        />
      ))}
      {[1, 2].map((i) => (
        <rect
          key={`h${i}`}
          x={innerX + 1}
          y={innerY + (innerH * i) / 3 - gridStroke / 2}
          width={innerW - 2}
          height={gridStroke}
          fill={frameColor}
          opacity="0.25"
          className="transition-all duration-500"
        />
      ))}

      <LeverHandle x={hx} y={hy} side={side} scale={handleScale} frameColor={frameColor} />
    </g>
  );
}

function LeverHandle({ x, y, side = "right", scale = 1, frameColor }) {
  const id = `lever-${Math.round(x)}-${Math.round(y)}-${side}`;
  const flip = side === "left" ? -1 : 1;

  return (
    <g
      transform={`translate(${x},${y}) scale(${flip * scale},${scale})`}
      pointerEvents="none"
    >
      <defs>
        <linearGradient id={`${id}-metal`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={frameColor} />
          <stop offset="50%" stopColor="#ccc" />
          <stop offset="100%" stopColor={frameColor} />
        </linearGradient>
      </defs>

      <g>
        <rect
          x="-10"
          y="-24"
          width="20"
          height="48"
          rx="8"
          fill={`url(#${id}-metal)`}
          stroke="rgba(0,0,0,0.3)"
          strokeWidth="0.5"
          className="transition-all duration-500"
        />
        <rect
          x="5"
          y="-6"
          width="8"
          height="12"
          rx="3"
          fill={`url(#${id}-metal)`}
          className="transition-all duration-500"
        />
      </g>

      <g transform={`translate(12,-6)`}>
        <rect
          x="0"
          y="0"
          width="14"
          height="72"
          rx="7"
          fill={`url(#${id}-metal)`}
          className="transition-all duration-500"
        />
        <rect
          x="2"
          y="2"
          width="10"
          height="68"
          rx="5"
          fill="rgba(0,0,0,0.18)"
        />
        <rect
          x="2"
          y="3"
          width="10"
          height="2"
          rx="1"
          fill="white"
          opacity="0.6"
        />
      </g>
    </g>
  );
}
